import Button from "@/components/Button";
import { arrow } from "@/designSystemIcons";
import { extractColors } from "@/functions/extractCardColors";
import { cn } from "@/lib/utils";
import { getVersionAssetsSelector } from "@/redux/loadedContent/loadedContentSlice";
import {
  generateNavigationUrl,
  versionUrlSlug,
} from "@/redux/players/playersSlice";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink } from "react-router";
import { useMediaQuery } from "usehooks-ts";

export type AlternativeVersionType = {
  rating: number;
  metalId: number;
  versionId: number;
  resourceId: number;
};

interface AlternativeVersionsSectionProps {
  alternativeVersions: AlternativeVersionType[];
  playerName: string;
  resourceId: number;
  hide: boolean;
  setIsAlternativeVersionsOpenMobile: (open: boolean) => void;
  isAlternativeVersionsOpenMobile: boolean;
}

interface AlternativeVersionProps {
  version: AlternativeVersionType;
  playerName: string;
  isActive: boolean;
}

export const AlternativeVersion = ({
  version,
  playerName,
  isActive,
}: AlternativeVersionProps) => {
  const versionAssets = useSelector(getVersionAssetsSelector);
  const versionSlug = versionUrlSlug(versionAssets[version.versionId]);
  const { cardUrl } = useMemo(() => {
    return extractColors({
      versionAssets,
      versionId: version.versionId,
      metalId: version.metalId,
    });
  }, [versionAssets, version.versionId, version.metalId]);

  return (
    <NavLink
      to={generateNavigationUrl({
        resourceId: version.resourceId,
        name: playerName,
        versionSlug,
        basePath: "/players",
      })}
      className={cn(
        "h-9 w-9 mobile:h-8 mobile:w-8 rounded flex items-center justify-center shadow-alternative-version relative shrink-0",
        !isActive && "opacity-50",
      )}
      key={version.resourceId}
    >
      <img
        src={cardUrl?.replace("cards/", "cards/square/")}
        alt={`${playerName} ${version.rating} ${version.versionId}`}
        className="w-full h-full object-cover absolute rounded"
      />
      <p
        className="text-white text-sm font-semibold z-10"
        style={{
          textShadow: "0px 1px 1px rgba(0, 0, 0, 0.80), 0px 1px 5.8px #000",
        }}
      >
        {version.rating}
      </p>
    </NavLink>
  );
};
const AlternativeVersionsSection = ({
  alternativeVersions,
  playerName,
  resourceId,
  hide,
  setIsAlternativeVersionsOpenMobile,
  isAlternativeVersionsOpenMobile,
}: AlternativeVersionsSectionProps) => {
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const { t } = useTranslation("common");
  const activeVersion = useMemo(() => {
    if (isMobile) {
      return alternativeVersions?.find(
        (version) => version.resourceId === resourceId,
      );
    }
  }, [isMobile, resourceId, alternativeVersions]);

  return (
    <div
      className={cn(
        "flex flex-row gap-2 col-span-1 transition-opacity duration-100",
        hide ? "opacity-0  pointer-events-none" : "opacity-100",
      )}
    >
      {isMobile
        ? activeVersion && (
            <>
              <AlternativeVersion
                key={activeVersion?.resourceId}
                version={activeVersion}
                playerName={playerName}
                isActive={true}
              />
              {alternativeVersions?.length > 1 && (
                <Button
                  variant={"tertiary"}
                  label={
                    isAlternativeVersionsOpenMobile ? (
                      <span className="text-dark-secondary text-xs w-7">
                        {t("hide")}
                      </span>
                    ) : (
                      <div className="rounded-full bg-action-tertiary-active text-dark-primary w-7 h-6 flex items-center justify-center text-xs">
                        +{alternativeVersions?.length}
                      </div>
                    )
                  }
                  onClick={() =>
                    setIsAlternativeVersionsOpenMobile(
                      !isAlternativeVersionsOpenMobile,
                    )
                  }
                  icon={
                    <div
                      className={cn(
                        "w-2 h-2",
                        isAlternativeVersionsOpenMobile && "rotate-180",
                      )}
                    >
                      {arrow}
                    </div>
                  }
                />
              )}
            </>
          )
        : [...(alternativeVersions ?? [])]
            .sort((a, b) => a.rating - b.rating)
            .map((version) => (
              <AlternativeVersion
                key={version.resourceId}
                version={version}
                playerName={playerName}
                isActive={version.resourceId === resourceId}
              />
            ))}
    </div>
  );
};

export default AlternativeVersionsSection;
